import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
  Collapse,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  CheckCircleOutline,
  AccessTime,
  Code as CodeIcon,
  History,
  KeyboardArrowDown,
  KeyboardArrowUp,
  ContentCopy,
} from "@mui/icons-material";
import Editor from "@monaco-editor/react";
import { getBaseEditorOptions } from "../../utils/programming/monacoEditorConfig";

const SubmittedProgrammingView = ({
  code,
  language,
  timeSpent,
  retakes,
  maxAttempts,
  languageName,
}) => {
  const theme = useTheme();
  const [showCode, setShowCode] = useState(true);

  // Format time (e.g., 125s -> "2m 5s")
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "#f8f9fa",
        overflowY: "auto",
        minHeight: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: "800px",
          p: { base: 2, md: 4 },
          borderRadius: "16px",
          border: "1px solid #e0e0e0",
          display: "flex",
          flexDirection: "column",
          gap: { base: 2, sm: 2.5, md: 3 },
          bgcolor: "#fff",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          mx: "auto",
        }}
      >
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 2,
              borderRadius: "50%",
              bgcolor: "#e8f5e9",
              mb: 2,
            }}
          >
            <CheckCircleOutline
              sx={{ fontSize: { base: "40px", md: "48px" }, color: "#2e7d32" }}
            />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#2d3748",
              mb: 1,
              fontSize: { base: "1.25rem", md: "1.5rem" },
            }}
          >
            Question Submitted Successfully
          </Typography>
          <Typography
            variant="body1"
            sx={{ color: "#718096", fontSize: { base: "14px", md: "16px" } }}
          >
            You have successfully completed this programming challenge. Your
            solution has been recorded.
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              base: "1fr",
              xs: "1fr 1fr",
              sm: "repeat(3, 1fr)",
            },
            gap: { base: 2, md: 3 },
            bgcolor: "#f7fafc",
            p: { base: 2, md: 3 },
            borderRadius: "12px",
            border: "1px solid #edf2f7",
          }}
        >
          {/* Time Taken */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#718096",
              }}
            >
              <AccessTime sx={{ fontSize: 18 }} />
              <Typography variant="caption" fontWeight={600}>
                TIME TAKEN
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "#2d3748",
                fontWeight: 700,
                fontSize: { base: "1.1rem", md: "1.25rem" },
              }}
            >
              {formatTime(timeSpent)}
            </Typography>
          </Box>

          {/* Language */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#718096",
              }}
            >
              <CodeIcon sx={{ fontSize: 18 }} />
              <Typography variant="caption" fontWeight={600}>
                LANGUAGE
              </Typography>
            </Box>
            <Chip
              label={languageName || language}
              size="small"
              sx={{
                bgcolor: "#e3f2fd",
                color: "#1565c0",
                fontWeight: 600,
                borderRadius: "6px",
                maxWidth: "100%",
                "& .MuiChip-label": {
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "block",
                  px: 1,
                },
              }}
            />
          </Box>

          {/* Attempts */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#718096",
              }}
            >
              <History sx={{ fontSize: 18 }} />
              <Typography variant="caption" fontWeight={600}>
                ATTEMPTS
              </Typography>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: "#2d3748",
                fontWeight: 700,
                fontSize: { base: "1.1rem", md: "1.25rem" },
              }}
            >
              {retakes}{" "}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { base: "0.8rem", md: "0.875rem" } }}
              >
                / {maxAttempts}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Code Preview Section */}
        <Box sx={{ mt: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              mb: 1.5,
              cursor: "pointer",
              pb: 0.5,
              borderBottom: "1px solid transparent",
              "&:hover": {
                borderBottom: "1px solid #edf2f7",
              },
            }}
            onClick={() => setShowCode(!showCode)}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#4a5568",
                letterSpacing: "0.5px",
                fontSize: { base: "12px", md: "13px" },
              }}
            >
              SUBMITTED SOLUTION
            </Typography>
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Copy Code">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyCode();
                  }}
                  sx={{ color: "#718096" }}
                >
                  <ContentCopy sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <IconButton size="small" sx={{ color: "#718096" }}>
                {showCode ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Stack>
          </Box>

          <Collapse in={showCode}>
            <Paper
              elevation={0}
              sx={{
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                overflow: "hidden",
                height: { base: "350px", sm: "400px", md: "450px" }, // More flexible height
                bgcolor: "#fff",
              }}
            >
              <Editor
                height="100%"
                language={language}
                value={code}
                theme="vs-light" // Provide a light theme for the review view
                options={{
                  ...getBaseEditorOptions(),
                  readOnly: true,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  lineNumbers: "on",
                  renderLineHighlight: "none",
                  domReadOnly: true,
                  contextmenu: false,
                }}
              />
            </Paper>
          </Collapse>
        </Box>
      </Paper>
    </Box>
  );
};

export default SubmittedProgrammingView;
